/**
 * Starter IconTile — the confident, on-brand square that leads a card.
 *
 * Replaces the washed-out 0.10-alpha tile each card composite used to
 * hand-roll. One control point for the leading-icon treatment so every
 * icon-card modernizes together and can't drift apart again. The fill is
 * derived from the tone's adaptive brand colour (light + dark correct) via
 * `color-mix`, with a faint same-hue ring for crisp depth — still flat, no
 * drop shadow (per the Clarify spike: borders, not shadows).
 *
 * Pass a Lucide icon (from `~/components/icons`) as the child. Size the icon
 * to match the tile: 20px for `sm`, 24px for `md`/`lg`.
 */
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import styles from './IconTile.module.css';

/**
 * Semantic tones — map to the app's adaptive `--tint-*-fg` brand colours.
 *
 * `brand` (teal) and `gold` (brass) are the on-brand vintage tones; the rest are
 * the ledger semantics (green/info/warning/error) plus neutral.
 */
export type IconTileTone =
  | 'brand'
  | 'gold'
  | 'green'
  | 'info'
  | 'neutral'
  | 'warning'
  | 'error';
export type IconTileSize = 'sm' | 'md' | 'lg';

export interface IconTileProps extends HTMLAttributes<HTMLSpanElement> {
  /** On-brand colour family for the tile fill + icon. Defaults to `green`. */
  readonly tone?: IconTileTone;
  /** 32px (`sm`) / 40px (`md`, default) / 48px (`lg`). */
  readonly size?: IconTileSize;
  /** The Lucide icon element. */
  readonly children: ReactNode;
}

export const IconTile = forwardRef<HTMLSpanElement, IconTileProps>(function IconTile(
  { tone = 'green', size = 'md', className, children, ...rest },
  ref,
) {
  const classes = [styles.root, styles[size], className].filter(Boolean).join(' ');
  return (
    <span ref={ref} className={classes} data-tone={tone} aria-hidden {...rest}>
      {children}
    </span>
  );
});
